import type { NavigationMenuItemExtendedPropsTypes } from 'src/components/Sidepanel/sidepanel-settings-modal';

/**
 * Render-time remapping of the nested device-settings nav tree.
 *
 * Input is `navigationItems` from generateNavigationItems(): top-level categories
 * with optional `innerNav` children (the old nested rail).
 *
 * Output:
 * - `detailsItem` — Details card (name / room / avatar). This is `details`
 *   (`device_details`), not `device_information` (Device Info / firmware).
 *   Device Info is a standalone rail row, not inside the Device settings group.
 * - `entries` — named section groups, then leftover rows that did not map.
 *   Advanced rows stay in the list so the rail can collapse them in place.
 *   Application extras (card customization, note, usage) go in Additional.
 *   Offline notifications go in Alerts; energy rows in Energy; pin lock in Safety.
 *   The old Safety category (power/voltage/current limits) is Device Protection.
 * - `actionItems` — reboot / soft reboot / reset / transfer / delete, stripped from groups.
 *   The rail no longer renders them as footer buttons; they live on Maintenance actions.
 */

export const SETTINGS_RAIL_SECTIONS = [
	'automations',
	'sensors',
	'connectivity',
	'alerts',
	'energy',
	'safety',
	'device_protection',
	'addons',
	'device_settings',
	'additional',
] as const;

export type SettingsRailSectionId = (typeof SETTINGS_RAIL_SECTIONS)[number];

export const SETTINGS_ACTION_TITLES = new Set([
	'device_reboot',
	'soft_reboot',
	'factory_reset',
	'factory_reset_g1',
	'factory_reset_ble',
	'factory_reset_z',
	'transfer_device',
	'delete_device',
]);

/**
 * Entire rail sections hidden unless Advanced is on.
 */
export const ADVANCED_SECTION_IDS = new Set<SettingsRailSectionId>(['addons', 'additional']);

/**
 * Hidden unless Advanced is on. Pattern: integrations, protocols, scripts,
 * and developer/diagnostic tools — not everyday Wi-Fi, schedules, or firmware.
 */
export const ADVANCED_SETTING_TITLES = new Set([
	'actions',
	'scripts',
	'virtual_components',
	'sensor_actions',
	'temperature_actions',
	'humidity_actions',
	'adc_actions',
	'addon_actions_settings',
	'mqtt',
	'local_network_messaging',
	'outbound_websocket',
	'rpc_over_udp',
	'range_extender',
	'restrict_login',
	'wifi_ap_roaming',
	'bluetooth_rpc_tester',
	'eco_mode',
	'high_frequency_mode',
	'enhanced_security',
	'operational_counters',
	'advanced_developer_settings',
	'input_state',
]);

/** Shown in the rail with a Premium gem; gated in the setting itself when the account is not Premium. */
export const PREMIUM_SETTING_TITLES = new Set([
	'ai_model',
	'exclude_forgotten_lights',
	'offline_notifications',
]);

/** Folded into Input/output when that row is on the same device (new layout only). */
export const IO_FOLDED_SETTING_TITLES = new Set([
	'enable_input',
	'enable_input_0',
	'enable_input_1',
	'lock_inputs',
	'invert_switch',
	'invert_input',
	'invert_input_0',
	'invert_input_1',
	'input_state',
]);

const IO_FOLDED_COMPONENT_KEYS = new Set([
	'enable_input_g2',
	'enable_input_per_id_g2',
	'enable_input_by_id_g2',
	'lock_inputs_g2',
	'invert_switch_g2',
	'invert_switch_per_id_g2',
	'input_invert_per_id_g2',
	'invert_input_by_id_g2',
	'input_state',
]);

const WALL_DISPLAY_IO_HUB_KEY = 'input_output_wall_display';

const WALL_DISPLAY_IO_FOLDED_TITLES = new Set(['input_type', 'output_type', 'action_on_power_on']);

const WALL_DISPLAY_IO_FOLDED_KEYS = new Set([
	'input_type_wall_display',
	'output_type_g2',
	'action_on_power_on_g2',
]);

const PLUG_IO_HUB_KEY = 'input_output_plug';

const PLUG_IO_FOLDED_TITLES = new Set(['detach_switch']);

const PLUG_IO_FOLDED_KEYS = new Set(['detach_switch_g2']);

const LED_HUB_KEY = 'led_indicators_plug';

const LED_FOLDED_TITLES = new Set(['led_indicator_color_mode', 'led_indicator_night_mode']);

const LED_FOLDED_KEYS = new Set([
	'led_indicator_color_plus_plug_s',
	'led_indicator_night_mode_plus_plug_s',
]);

const BEHAVIOR_HUB_KEY = 'behavior_blu_trv';

const CALIBRATION_FOLDED_TITLES = new Set(['calibration']);

const CALIBRATION_FOLDED_KEYS = new Set(['calibration_blu_trv']);

const BOOST_HUB_KEY = 'boost_blu_trv';

const BOOST_FOLDED_TITLES = new Set([
	'trv_boost_duration_override',
	'trv_boost_open_window_target_temperature',
]);

const BOOST_FOLDED_KEYS = new Set(['boost_duration_override', 'open_window_target_temperature']);

const DIMMING_HUB_KEY = 'dimming_behavior_g2';

const DIMMING_FOLDED_TITLES = new Set([
	'button_fade_rate',
	'min_brightness_on_toggle',
	'transition_duration',
]);

const DIMMING_FOLDED_KEYS = new Set([
	'button_fade_rate_g2',
	'min_brightness_on_toggle_g2',
	'transition_g2',
]);

const CAMERA_PICTURE_HUB_KEY = 'camera_picture';

const CAMERA_PICTURE_FOLDED_TITLES = new Set(['camera_video_adjustments', 'camera_image_adjustments']);

const CAMERA_PICTURE_FOLDED_KEYS = new Set(['camera_video_adjustments', 'camera_image_adjustments']);

const TEMPERATURE_SENSOR_HUB_KEY = 'temperature_settings_g2';

const TEMPERATURE_SENSOR_FOLDED_TITLES = new Set([
	'temperature_unit',
	'temperature_offset',
	'temperature_threshold',
]);

const TEMPERATURE_SENSOR_FOLDED_KEYS = new Set([
	'temperature_unit_g2',
	'temperature_offset_g2',
	'temperature_threshold_g2',
]);

const HUMIDITY_SENSOR_HUB_KEY = 'humidity_settings_g2';

const HUMIDITY_SENSOR_FOLDED_TITLES = new Set(['humidity_offset', 'humidity_threshold']);

const HUMIDITY_SENSOR_FOLDED_KEYS = new Set(['humidity_offset_g2', 'humidity_threshold_g2']);

const EMETER_LED_HUB_KEY = 'emeter_led_indicator';

const EMETER_LED_FOLDED_TITLES = new Set(['blink_mode_selector', 'led_phase_selector']);

const EMETER_LED_FOLDED_KEYS = new Set(['blink_mode_selector', 'phase_selector']);

const EMETER_MEASUREMENT_HUB_KEY = 'emeter_measurement';

const EMETER_MEASUREMENT_FOLDED_TITLES = new Set([
	'reverse_measurement_direction',
	'change_ct_type',
	'monitor_phase_sequence',
]);

const EMETER_MEASUREMENT_FOLDED_KEYS = new Set([
	'reverse_measurement_direction_g2',
	'reverse_measurement_direction_monophase',
	'change_ct_type',
	'monitor_phase_sequence_g2',
]);

const EMETER_CALIBRATION_HUB_KEY = 'emeter_calibration';

const EMETER_CALIBRATION_FOLDED_TITLES = new Set([
	'phase_calibration',
	'phase_calibration_reset',
	'channel_from_channel_calibration',
]);

const EMETER_CALIBRATION_FOLDED_KEYS = new Set([
	'phase_to_phase_g2',
	'phase_calibration_reset_g2',
	'channel_from_channel_calibration_g2',
]);

const EMETER_STORED_DATA_HUB_KEY = 'emeter_stored_data';

const EMETER_STORED_DATA_FOLDED_TITLES = new Set(['delete_stored_data', 'reset_energy_counters']);

const EMETER_STORED_DATA_FOLDED_KEYS = new Set(['delete_stored_data_g2', 'reset_energy_counters_g2']);

/**
 * Folded settings titles → hub nav ids. Deep-links still use the old public API
 * (`navigateToSetting(id, setting, subSetting)` with config titles). The modal
 * resolves against the visible flattened rail, not the nested config tree.
 */
export const SETTINGS_RAIL_FOLD_ALIASES: Record<string, string> = {
	calibration: 'trv_behaviour',
	trv_boost_duration_override: 'trv_boost',
	trv_boost_open_window_target_temperature: 'trv_boost',
	led_indicator_color_mode: 'led_indicators',
	led_indicator_night_mode: 'led_indicators',
	button_fade_rate: 'dimming',
	min_brightness_on_toggle: 'dimming',
	transition_duration: 'dimming',
	camera_video_adjustments: 'camera_picture',
	camera_image_adjustments: 'camera_picture',
	temperature_unit: 'temperature_settings',
	temperature_offset: 'temperature_settings',
	temperature_threshold: 'temperature_settings',
	humidity_offset: 'humidity_settings',
	humidity_threshold: 'humidity_settings',
	blink_mode_selector: 'emeter_led_indicator',
	led_phase_selector: 'emeter_led_indicator',
	reverse_measurement_direction: 'emeter_measurement',
	change_ct_type: 'emeter_measurement',
	monitor_phase_sequence: 'emeter_measurement',
	phase_calibration: 'emeter_calibration',
	phase_calibration_reset: 'emeter_calibration',
	channel_from_channel_calibration: 'emeter_calibration',
	delete_stored_data: 'emeter_stored_data',
	reset_energy_counters: 'emeter_stored_data',
	enable_input: 'input_output',
	enable_input_0: 'input_output',
	enable_input_1: 'input_output',
	lock_inputs: 'input_output',
	invert_switch: 'input_output',
	invert_input: 'input_output',
	invert_input_0: 'input_output',
	invert_input_1: 'input_output',
	input_state: 'input_output',
	input_type: 'input_output',
	output_type: 'input_output',
	action_on_power_on: 'input_output',
	detach_switch: 'input_output',
	soft_reboot: 'maintenance_actions',
};

/**
 * Map a requested settings title to a visible rail nav id.
 * Prefer the requested id when it is still on the rail (old layout); otherwise
 * use the fold alias when that hub is present.
 */
export function resolveSettingsRailNavId(
	requestedId: string,
	itemExists: (id: string) => boolean,
): string {
	if (!requestedId) return requestedId;
	if (itemExists(requestedId)) return requestedId;
	const aliased = SETTINGS_RAIL_FOLD_ALIASES[requestedId];
	if (aliased && itemExists(aliased)) return aliased;
	return requestedId;
}

const RAIL_ROW_TITLE_OVERRIDES: Record<string, { key: string; default: string }> = {
	timer: { key: 'settings_row_auto_on_off', default: 'Auto On/Off' },
	wifi_mode_client: { key: 'settings_row_primary_wifi', default: 'Primary Wi-Fi' },
	wifi_client_backup: { key: 'settings_row_backup_wifi', default: 'Backup Wi-Fi' },
	ai_model: { key: 'settings_row_energy_ai_insights', default: 'Energy AI insights' },
	geo_location_and_time_zone: {
		key: 'settings_row_location_and_time',
		default: 'Location and time zone',
	},
	appliance_type: { key: 'settings_row_energy_category', default: 'Energy category' },
	led_indicators: { key: 'settings_row_led_indicators', default: 'LED indicators' },
	dimming: { key: 'settings_row_dimming', default: 'Dimming' },
	button_presets: { key: 'settings_row_double_press_preset', default: 'Double-press preset' },
	high_frequency_mode: {
		key: 'settings_row_reduce_pwm_noise',
		default: 'Reduce PWM noise',
	},
	bluetooth_settings: { key: 'settings_row_bluetooth', default: 'Bluetooth' },
	temperature_favorite_positions: {
		key: 'settings_row_favorite_positions',
		default: 'Favorite positions',
	},
	trv_boost: { key: 'settings_row_trv_boost', default: 'Boost' },
	over_under_voltage_auto_recovery: {
		key: 'settings_row_voltage_protection',
		default: 'Voltage protection',
	},
	overvoltage_auto_recovery: {
		key: 'settings_row_voltage_protection',
		default: 'Voltage protection',
	},
	under_voltage_protection: {
		key: 'settings_row_voltage_protection',
		default: 'Voltage protection',
	},
	voltage_protection: {
		key: 'settings_row_voltage_protection',
		default: 'Voltage protection',
	},
	camera_picture: { key: 'settings_row_camera_picture', default: 'Image & video' },
	emeter_led_indicator: { key: 'settings_row_emeter_led_indicator', default: 'LED indicator' },
	emeter_measurement: { key: 'settings_row_emeter_measurement', default: 'Measurement' },
	emeter_calibration: { key: 'settings_row_emeter_calibration', default: 'Calibration' },
	emeter_stored_data: {
		key: 'settings_row_emeter_stored_data',
		default: 'Stored energy data',
	},
	emeter_alarms: { key: 'settings_row_emeter_alarms', default: 'Meter alarms' },
};

/** Old top-level category `title` → flattened section. Children become rows. */
const PARENT_TO_SECTION: Record<string, SettingsRailSectionId> = {
	timer: 'automations',
	schedule: 'automations',
	actions: 'automations',
	scripts: 'automations',
	night_mode: 'automations',
	sensor_settings: 'sensors',
	networks: 'connectivity',
	bluetooth: 'connectivity',
	internet: 'connectivity',
	safety: 'device_protection',
	addon_sensors: 'addons',
	settings: 'device_settings',
	bluetooth_gateway: 'device_settings',
	camera_zones: 'device_settings',
	camera_and_video: 'device_settings',
	camera_audio: 'device_settings',
	camera_storage: 'device_settings',
};

const ACTION_ITEM_ORDER: Record<string, number> = {
	device_reboot: 0,
	soft_reboot: 1,
	factory_reset: 2,
	factory_reset_g1: 2,
	factory_reset_ble: 2,
	factory_reset_z: 2,
	transfer_device: 3,
	delete_device: 4,
};

const TITLE_TO_SECTION: Record<string, SettingsRailSectionId> = {
	exclude_event_log: 'alerts',
	exclude_forgotten_lights: 'alerts',
	offline_notifications: 'alerts',
	appliance_type: 'energy',
	ai_model: 'energy',
	consumption_calculation: 'energy',
	consumption_hierarchy: 'energy',
	consumption_correction: 'energy',
	consumption_indicator: 'energy',
	power_consumption: 'energy',
	emeter_measurement: 'energy',
	reverse_measurement_direction: 'energy',
	change_ct_type: 'energy',
	monitor_phase_sequence: 'energy',
	emeter_calibration: 'energy',
	phase_calibration: 'energy',
	phase_calibration_reset: 'energy',
	channel_from_channel_calibration: 'energy',
	emeter_stored_data: 'energy',
	delete_stored_data: 'energy',
	reset_energy_counters: 'energy',
	emeter_alarms: 'energy',
	pin_lock: 'safety',
	kids_protection: 'safety',
	device_card_customization: 'additional',
	device_note: 'additional',
	device_usage_details: 'additional',
};

const ADDITIONAL_ITEM_ORDER: Record<string, number> = {
	device_card_customization: 0,
	device_note: 1,
	device_usage_details: 2,
};

/** Firmware Version stays in Device settings but is pinned to the top of that group. */
const DEVICE_SETTINGS_ITEM_ORDER: Record<string, number> = {
	firmware_version: 0,
};

/** Rows that must not appear in the rail (name/room live on Details; clone-config is parked). */
const HIDE_FROM_RAIL = new Set(['device_name', 'location', 'apply_settings_configuration']);

export const SETTINGS_SECTION_COPY: Record<
	SettingsRailSectionId,
	{ titleKey: string; titleDefault: string; infoKey: string; infoDefault: string }
> = {
	automations: {
		titleKey: 'settings_title_automations',
		titleDefault: 'Automations',
		infoKey: 'settings_section_desc_automations',
		infoDefault: 'Auto On/Off, schedules, night mode, actions, and scripts.',
	},
	sensors: {
		titleKey: 'settings_title_sensors',
		titleDefault: 'Sensors',
		infoKey: 'settings_section_desc_sensors',
		infoDefault: 'Temperature, calibration, and attached sensors.',
	},
	connectivity: {
		titleKey: 'settings_title_connectivity',
		titleDefault: 'Connectivity',
		infoKey: 'settings_section_desc_connectivity',
		infoDefault: 'Network, Wi-Fi, Bluetooth, and MQTT settings.',
	},
	alerts: {
		titleKey: 'settings_title_alerts',
		titleDefault: 'Alerts',
		infoKey: 'settings_section_desc_alerts',
		infoDefault: 'Notification-related settings for this device.',
	},
	energy: {
		titleKey: 'settings_title_energy',
		titleDefault: 'Energy',
		infoKey: 'settings_section_desc_energy',
		infoDefault: 'Energy category, metering, stored data, and AI insights.',
	},
	safety: {
		titleKey: 'settings_title_safety',
		titleDefault: 'Safety',
		infoKey: 'settings_section_desc_safety',
		infoDefault: 'PIN lock and related safety options.',
	},
	device_protection: {
		titleKey: 'settings_title_device_protection',
		titleDefault: 'Device Protection',
		infoKey: 'settings_section_desc_device_protection',
		infoDefault: 'Power, voltage, and current limits.',
	},
	addons: {
		titleKey: 'settings_title_addons',
		titleDefault: 'Add-ons',
		infoKey: 'settings_section_desc_addons',
		infoDefault: 'Optional peripherals connected to this device.',
	},
	device_settings: {
		titleKey: 'settings_title_settings',
		titleDefault: 'Device settings',
		infoKey: 'settings_section_desc_device_settings',
		infoDefault: 'Core device configuration.',
	},
	additional: {
		titleKey: 'settings_title_additional',
		titleDefault: 'Additional',
		infoKey: 'settings_section_desc_additional',
		infoDefault: 'Card customization, notes, and usage details.',
	},
};

export type FlattenedRailEntry =
	| {
			kind: 'group';
			id: SettingsRailSectionId;
			items: NavigationMenuItemExtendedPropsTypes[];
	  }
	| { kind: 'row'; item: NavigationMenuItemExtendedPropsTypes };

export function collectSettingsRailNavIds(rail: {
	entries: FlattenedRailEntry[];
	detailsItem?: NavigationMenuItemExtendedPropsTypes;
}): Set<string> {
	const ids = new Set<string>();
	const add = (item?: Pick<NavigationMenuItemExtendedPropsTypes, 'id' | 'title'>) => {
		if (!item) return;
		if (item.id) ids.add(item.id);
		if (item.title) ids.add(item.title);
	};

	add(rail.detailsItem);
	for (const entry of rail.entries) {
		if (entry.kind === 'group') {
			for (const row of entry.items) add(row);
		} else {
			add(entry.item);
		}
	}

	return ids;
}

function isActionItem(item: NavigationMenuItemExtendedPropsTypes): boolean {
	return SETTINGS_ACTION_TITLES.has(item.title);
}

function isHiddenFromRail(item: NavigationMenuItemExtendedPropsTypes): boolean {
	return HIDE_FROM_RAIL.has(item.title);
}

function matchesFoldedRow(
	item: NavigationMenuItemExtendedPropsTypes,
	keys: Set<string>,
	titles: Set<string>,
): boolean {
	const componentKey = item.component?.componentKey;
	if (typeof componentKey === 'string' && keys.has(componentKey)) return true;
	return titles.has(item.title);
}

function isIoFoldedRailRow(
	item: NavigationMenuItemExtendedPropsTypes,
	hasWallDisplayHub: boolean,
): boolean {
	const componentKey = item.component?.componentKey;
	if (typeof componentKey === 'string') {
		if (IO_FOLDED_COMPONENT_KEYS.has(componentKey)) return true;
		return hasWallDisplayHub && WALL_DISPLAY_IO_FOLDED_KEYS.has(componentKey);
	}
	if (IO_FOLDED_SETTING_TITLES.has(item.title)) return true;
	return hasWallDisplayHub && WALL_DISPLAY_IO_FOLDED_TITLES.has(item.title);
}

function isDetailsItem(item: NavigationMenuItemExtendedPropsTypes): boolean {
	return item.title === 'details' || item.id === 'details';
}

/** Children of a category, minus actions and hidden rows. Leaf if there is no innerNav. */
function collectLeaves(
	parent: NavigationMenuItemExtendedPropsTypes,
): NavigationMenuItemExtendedPropsTypes[] {
	if (parent.innerNav?.length) {
		return parent.innerNav.filter((child) => !isActionItem(child) && !isHiddenFromRail(child));
	}
	if (isHiddenFromRail(parent) || isActionItem(parent)) return [];
	return [parent];
}

function collectActions(
	item: NavigationMenuItemExtendedPropsTypes,
): NavigationMenuItemExtendedPropsTypes[] {
	const fromChildren = item.innerNav?.filter(isActionItem) ?? [];
	return isActionItem(item) ? [item, ...fromChildren] : fromChildren;
}

export function getSettingsRailSectionForItem(
	entries: FlattenedRailEntry[],
	item?: Pick<NavigationMenuItemExtendedPropsTypes, 'id' | 'title'>,
): SettingsRailSectionId | undefined {
	if (!item) return undefined;

	for (const entry of entries) {
		if (entry.kind !== 'group') continue;
		const inSection = entry.items.some(
			(row) => (item.id && row.id === item.id) || (item.title && row.title === item.title),
		);
		if (inSection) return entry.id;
	}

	return undefined;
}

export function getSettingsRailRowTitle(item: NavigationMenuItemExtendedPropsTypes): {
	key: string;
	default: string;
} {
	const override = RAIL_ROW_TITLE_OVERRIDES[item.title];
	if (override) return override;
	if (item.parentID) {
		return {
			key: `sub_settings_title_${item.title}`,
			default: item.title.replace(/_/g, ' '),
		};
	}
	return {
		key: `settings_title_${item.title}`,
		default: item.title.replace(/_/g, ' '),
	};
}

export function isSettingsRailRowPremium(
	item: Pick<NavigationMenuItemExtendedPropsTypes, 'title' | 'isPremiumSetting'>,
): boolean {
	return Boolean(item.isPremiumSetting) || PREMIUM_SETTING_TITLES.has(item.title);
}

export function getSettingsRailPremiumState(
	item: Pick<NavigationMenuItemExtendedPropsTypes, 'title' | 'isPremiumSetting'>,
	accountIsPremium: boolean,
): 'upsell' | 'owned' | undefined {
	if (!isSettingsRailRowPremium(item)) return undefined;
	return accountIsPremium ? 'owned' : 'upsell';
}

export function isSettingsRailItemAdvanced(
	item: Pick<NavigationMenuItemExtendedPropsTypes, 'title'>,
	sectionId?: SettingsRailSectionId,
): boolean {
	return (
		ADVANCED_SETTING_TITLES.has(item.title) ||
		(sectionId != null && ADVANCED_SECTION_IDS.has(sectionId))
	);
}

export function isSettingsRailRowCollapsed(
	item: Pick<NavigationMenuItemExtendedPropsTypes, 'title'>,
	showAdvanced: boolean,
	sectionId?: SettingsRailSectionId,
): boolean {
	return !showAdvanced && isSettingsRailItemAdvanced(item, sectionId);
}

export function isSettingsRailGroupCollapsed(
	items: Pick<NavigationMenuItemExtendedPropsTypes, 'title'>[],
	showAdvanced: boolean,
	sectionId?: SettingsRailSectionId,
): boolean {
	if (sectionId && ADVANCED_SECTION_IDS.has(sectionId)) {
		return !showAdvanced && items.length > 0;
	}
	return (
		items.length > 0 &&
		items.every((item) => isSettingsRailRowCollapsed(item, showAdvanced, sectionId))
	);
}

export function flattenSettingsRail(
	items: NavigationMenuItemExtendedPropsTypes[],
	options?: { showAdvanced?: boolean; showNewLayout?: boolean },
): {
	entries: FlattenedRailEntry[];
	actionItems: NavigationMenuItemExtendedPropsTypes[];
	detailsItem: NavigationMenuItemExtendedPropsTypes | undefined;
} {
	const buckets: Record<SettingsRailSectionId, NavigationMenuItemExtendedPropsTypes[]> = {
		automations: [],
		sensors: [],
		connectivity: [],
		alerts: [],
		energy: [],
		safety: [],
		device_protection: [],
		addons: [],
		device_settings: [],
		additional: [],
	};
	let unmapped: NavigationMenuItemExtendedPropsTypes[] = [];
	const actionItems: NavigationMenuItemExtendedPropsTypes[] = [];
	let detailsItem: NavigationMenuItemExtendedPropsTypes | undefined;

	const routeLeaf = (
		leaf: NavigationMenuItemExtendedPropsTypes,
		fallback: SettingsRailSectionId | 'unmapped',
	) => {
		const byTitle = TITLE_TO_SECTION[leaf.title];
		if (byTitle) {
			buckets[byTitle].push(leaf);
		} else if (fallback === 'unmapped') {
			unmapped.push(leaf);
		} else {
			buckets[fallback].push(leaf);
		}
	};

	for (const item of items) {
		actionItems.push(...collectActions(item));

		if (isDetailsItem(item)) {
			detailsItem = item;
			continue;
		}

		if (item.title === 'application_settings') {
			for (const child of collectLeaves(item)) {
				routeLeaf(child, 'device_settings');
			}
			continue;
		}

		const section = PARENT_TO_SECTION[item.title];
		if (section) {
			for (const leaf of collectLeaves(item)) {
				routeLeaf(leaf, section);
			}
		} else if (!isActionItem(item) && !isHiddenFromRail(item)) {
			for (const leaf of collectLeaves(item)) {
				routeLeaf(leaf, 'unmapped');
			}
		}
	}

	const hasInputOutput = Object.values(buckets).some((rows) =>
		rows.some((row) => row.title === 'input_output'),
	);
	const hasWallDisplayHub = Object.values(buckets).some((rows) =>
		rows.some((row) => row.component?.componentKey === WALL_DISPLAY_IO_HUB_KEY),
	);
	const hasPlugIoHub = Object.values(buckets).some((rows) =>
		rows.some((row) => row.component?.componentKey === PLUG_IO_HUB_KEY),
	);
	const hasLedHub = Object.values(buckets).some((rows) =>
		rows.some(
			(row) => row.component?.componentKey === LED_HUB_KEY || row.title === 'led_indicators',
		),
	);
	const hasDimmingHub = Object.values(buckets).some((rows) =>
		rows.some((row) => row.component?.componentKey === DIMMING_HUB_KEY || row.title === 'dimming'),
	);
	const hasBehaviorHub = Object.values(buckets).some((rows) =>
		rows.some((row) => row.component?.componentKey === BEHAVIOR_HUB_KEY),
	);
	const hasBoostHub = Object.values(buckets).some((rows) =>
		rows.some((row) => row.component?.componentKey === BOOST_HUB_KEY || row.title === 'trv_boost'),
	);
	const hasCameraPictureHub = [...Object.values(buckets).flat(), ...unmapped].some(
		(row) =>
			row.component?.componentKey === CAMERA_PICTURE_HUB_KEY || row.title === 'camera_picture',
	);
	const hasTemperatureSensorHub = Object.values(buckets).some((rows) =>
		rows.some((row) => row.component?.componentKey === TEMPERATURE_SENSOR_HUB_KEY),
	);
	const hasHumiditySensorHub = Object.values(buckets).some((rows) =>
		rows.some((row) => row.component?.componentKey === HUMIDITY_SENSOR_HUB_KEY),
	);
	const allRows = [...Object.values(buckets).flat(), ...unmapped];
	const hasEmeterLedHub = allRows.some(
		(row) =>
			row.component?.componentKey === EMETER_LED_HUB_KEY || row.title === 'emeter_led_indicator',
	);
	const hasEmeterMeasurementHub = allRows.some(
		(row) =>
			row.component?.componentKey === EMETER_MEASUREMENT_HUB_KEY ||
			row.title === 'emeter_measurement',
	);
	const hasEmeterCalibrationHub = allRows.some(
		(row) =>
			row.component?.componentKey === EMETER_CALIBRATION_HUB_KEY ||
			row.title === 'emeter_calibration',
	);
	const hasEmeterStoredDataHub = allRows.some(
		(row) =>
			row.component?.componentKey === EMETER_STORED_DATA_HUB_KEY ||
			row.title === 'emeter_stored_data',
	);

	const shouldKeepNewLayoutRow = (row: NavigationMenuItemExtendedPropsTypes) => {
		if (hasInputOutput && isIoFoldedRailRow(row, hasWallDisplayHub)) return false;
		if (hasPlugIoHub && matchesFoldedRow(row, PLUG_IO_FOLDED_KEYS, PLUG_IO_FOLDED_TITLES)) {
			return false;
		}
		if (hasLedHub && matchesFoldedRow(row, LED_FOLDED_KEYS, LED_FOLDED_TITLES)) {
			return false;
		}
		if (hasDimmingHub && matchesFoldedRow(row, DIMMING_FOLDED_KEYS, DIMMING_FOLDED_TITLES)) {
			return false;
		}
		if (
			hasBehaviorHub &&
			matchesFoldedRow(row, CALIBRATION_FOLDED_KEYS, CALIBRATION_FOLDED_TITLES)
		) {
			return false;
		}
		if (hasBoostHub && matchesFoldedRow(row, BOOST_FOLDED_KEYS, BOOST_FOLDED_TITLES)) {
			return false;
		}
		if (
			hasCameraPictureHub &&
			matchesFoldedRow(row, CAMERA_PICTURE_FOLDED_KEYS, CAMERA_PICTURE_FOLDED_TITLES)
		) {
			return false;
		}
		if (
			hasTemperatureSensorHub &&
			matchesFoldedRow(row, TEMPERATURE_SENSOR_FOLDED_KEYS, TEMPERATURE_SENSOR_FOLDED_TITLES)
		) {
			return false;
		}
		if (
			hasHumiditySensorHub &&
			matchesFoldedRow(row, HUMIDITY_SENSOR_FOLDED_KEYS, HUMIDITY_SENSOR_FOLDED_TITLES)
		) {
			return false;
		}
		if (hasEmeterLedHub && matchesFoldedRow(row, EMETER_LED_FOLDED_KEYS, EMETER_LED_FOLDED_TITLES)) {
			return false;
		}
		if (
			hasEmeterMeasurementHub &&
			matchesFoldedRow(row, EMETER_MEASUREMENT_FOLDED_KEYS, EMETER_MEASUREMENT_FOLDED_TITLES)
		) {
			return false;
		}
		if (
			hasEmeterCalibrationHub &&
			matchesFoldedRow(row, EMETER_CALIBRATION_FOLDED_KEYS, EMETER_CALIBRATION_FOLDED_TITLES)
		) {
			return false;
		}
		if (
			hasEmeterStoredDataHub &&
			matchesFoldedRow(row, EMETER_STORED_DATA_FOLDED_KEYS, EMETER_STORED_DATA_FOLDED_TITLES)
		) {
			return false;
		}
		return true;
	};

	const isOldLayoutHub = (row: NavigationMenuItemExtendedPropsTypes) =>
		row.component?.componentKey === WALL_DISPLAY_IO_HUB_KEY ||
		row.component?.componentKey === LED_HUB_KEY ||
		row.component?.componentKey === DIMMING_HUB_KEY ||
		row.component?.componentKey === BOOST_HUB_KEY ||
		row.component?.componentKey === CAMERA_PICTURE_HUB_KEY ||
		row.component?.componentKey === TEMPERATURE_SENSOR_HUB_KEY ||
		row.component?.componentKey === HUMIDITY_SENSOR_HUB_KEY ||
		row.component?.componentKey === EMETER_LED_HUB_KEY ||
		row.component?.componentKey === EMETER_MEASUREMENT_HUB_KEY ||
		row.component?.componentKey === EMETER_CALIBRATION_HUB_KEY ||
		row.component?.componentKey === EMETER_STORED_DATA_HUB_KEY;

	if (options?.showNewLayout) {
		for (const id of SETTINGS_RAIL_SECTIONS) {
			buckets[id] = buckets[id].filter(shouldKeepNewLayoutRow);
		}
		unmapped = unmapped.filter(shouldKeepNewLayoutRow);
	} else {
		for (const id of SETTINGS_RAIL_SECTIONS) {
			buckets[id] = buckets[id].filter((row) => !isOldLayoutHub(row));
		}
		unmapped = unmapped.filter((row) => !isOldLayoutHub(row));
	}

	const hasBluetoothSettings = Object.values(buckets).some((rows) =>
		rows.some((row) => row.title === 'bluetooth_settings'),
	);
	if (hasBluetoothSettings) {
		for (const id of SETTINGS_RAIL_SECTIONS) {
			buckets[id] = buckets[id].filter((row) => row.title !== 'assigned_bluetooth_devices');
		}
	}

	const entries: FlattenedRailEntry[] = [];

	for (const id of SETTINGS_RAIL_SECTIONS) {
		const rows = buckets[id];
		if (rows.length > 0) {
			if (id === 'additional') {
				rows.sort(
					(a, b) =>
						(ADDITIONAL_ITEM_ORDER[a.title] ?? 99) - (ADDITIONAL_ITEM_ORDER[b.title] ?? 99),
				);
			}
			if (id === 'device_settings') {
				rows.sort(
					(a, b) =>
						(DEVICE_SETTINGS_ITEM_ORDER[a.title] ?? 99) -
						(DEVICE_SETTINGS_ITEM_ORDER[b.title] ?? 99),
				);
			}
			entries.push({ kind: 'group', id, items: rows });
		}
	}

	for (const item of unmapped) {
		entries.push({ kind: 'row', item });
	}

	return {
		entries,
		actionItems: [...actionItems].sort(
			(a, b) => (ACTION_ITEM_ORDER[a.title] ?? 99) - (ACTION_ITEM_ORDER[b.title] ?? 99),
		),
		detailsItem,
	};
}
